import React, { useState, useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { Loader, AlertBanner } from '@saviynt/design-system';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getSourceConfig } from '../../utils/migrationUtils';
import { STATUS } from '../../constants/migrationConstants';
import {
  TITLES,
  DESCRIPTIONS,
  UI_LABELS,
  OBJECT_TYPE_LABELS,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES,
} from '../../constants/stringConstants';
import {
  fetchDataMigrationObjects,
  bulkDataMigrate,
  migrateIdentities,
  fetchSourceStatus,
} from '../../redux/migrationThunks';
import DataTypeCard from './DataTypeCard';
import ApplicationSelector from './ApplicationSelector';
import ExecuteDialog from './ExecuteDialog';
import useStyles from './styles/index.styles';

function DataMigration({ sourceType, sourceID }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { sourceStatus } = useSelector((state) => state.migration ?? {});
  const identityMigrationCompleted = !!sourceStatus?.identityMigrationCompleted;
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [workgroups, setWorkgroups] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [selectedWorkgroups, setSelectedWorkgroups] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showApplications, setShowApplications] = useState(false);
  const [showWorkgroups, setShowWorkgroups] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migratingWorkgroups, setMigratingWorkgroups] = useState(false);
  const [identityMigrationStatus, setIdentityMigrationStatus] = useState(null);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [banner, setBanner] = useState({ visible: false, type: 'Success', message: '' });
  const config = getSourceConfig(sourceType);

  const allAppsMigrated =
    applications.length > 0 &&
    applications.every(
      (app) =>
        app.dataMigrationStatus !== STATUS.STATUS_PENDING &&
        app.dataMigrationStatus !== undefined &&
        app.dataMigrationStatus !== null
    );
  const isApplicationsSelected = selectedTypes.includes('applications');

  useEffect(() => {
    if (!sourceID) return;
    const fetchApplications = async () => {
      try {
        setLoading(true);

        const appsData = await dispatch(
          fetchDataMigrationObjects({
            sourceId: sourceID,
            objectType: OBJECT_TYPE_LABELS.APPLICATION,
          })
        ).unwrap();
        const completedApps = appsData?.data
          ? appsData?.data.filter((app) => app.status === STATUS.STATUS_COMPLETED)
          : [];

        setApplications(completedApps);

        const wgData = await dispatch(
          fetchDataMigrationObjects({
            sourceId: sourceID,
            objectType: OBJECT_TYPE_LABELS.WORKGROUP,
          })
        ).unwrap();
        const completedWgs = wgData?.data
          ? wgData.data.filter((wg) => wg.status === STATUS.STATUS_COMPLETED)
          : [];
        setWorkgroups(completedWgs);
      } catch (error) {
        setApplications([]);
        setWorkgroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [sourceID, dispatch]);

  useEffect(() => {
    if (allAppsMigrated && isApplicationsSelected) {
      setSelectedTypes((prev) => prev.filter((t) => t !== 'applications'));
      setSelectedApps([]);
    }
  }, [allAppsMigrated, isApplicationsSelected]);

  const handleAppSelect = (appId) => {
    setSelectedApps((prev) => {
      const newSelection = prev.includes(appId)
        ? prev.filter((id) => id !== appId)
        : [...prev, appId];
      if (newSelection.length === 0) {
        setSelectedTypes((prevTypes) => prevTypes.filter((t) => t !== 'applications'));
      } else if (!selectedTypes.includes('applications')) {
        setSelectedTypes((prevTypes) => [...prevTypes, 'applications']);
      }
      return newSelection;
    });
  };

  const allWorkgroupsMigrated =
    workgroups.length > 0 &&
    workgroups.every(
      (wg) =>
        wg.dataMigrationStatus !== STATUS.STATUS_PENDING &&
        wg.dataMigrationStatus !== undefined &&
        wg.dataMigrationStatus !== null
    );

  const handleWorkgroupSelect = (wgId) => {
    setSelectedWorkgroups((prev) => {
      const newSelection = prev.includes(wgId) ? prev.filter((id) => id !== wgId) : [...prev, wgId];
      if (newSelection.length === 0) {
        setSelectedTypes((prevTypes) => prevTypes.filter((t) => t !== 'workgroups'));
      } else if (!selectedTypes.includes('workgroups')) {
        setSelectedTypes((prevTypes) => [...prevTypes, 'workgroups']);
      }
      return newSelection;
    });
  };

  const handleClearAll = () => {
    setSelectedTypes([]);
    setSelectedApps([]);
    setSelectedWorkgroups([]);
  };

  const handleMigrate = async () => {
    if (selectedApps.length === 0) {
      return;
    }
    setShowExecuteDialog(true);
  };

  const handleWorkgroupMigrate = async () => {
    if (selectedWorkgroups.length === 0) return;
    setMigratingWorkgroups(true);
    try {
      const objectIds = selectedWorkgroups.map((id) => parseInt(id, 10));
      const response = await dispatch(
        bulkDataMigrate({
          sourceId: sourceID,
          objectType: OBJECT_TYPE_LABELS.WORKGROUP,
          objectIds,
          executeJobTriggers: true,
        })
      ).unwrap();

      const isPending =
        response?.message?.includes('Import Job Created') ||
        response?.message?.includes('Job Created');

      setWorkgroups((prev) =>
        prev.map((wg) =>
          selectedWorkgroups.includes(wg.id)
            ? {
                ...wg,
                dataMigrationStatus: isPending
                  ? STATUS.STATUS_IN_PROGRESS
                  : STATUS.STATUS_COMPLETED,
              }
            : wg
        )
      );
      setSelectedWorkgroups([]);
      setSelectedTypes((prev) => prev.filter((t) => t !== 'workgroups'));
      setBanner({
        visible: true,
        type: 'Success',
        message: 'Workgroup data migration completed successfully.',
      });
    } catch (error) {
      const results = error?.data?.data?.results || error?.data?.results || [];
      if (results.length > 0) {
        setWorkgroups((prev) =>
          prev.map((wg) => {
            const result = results.find((r) => String(r.objectId) === String(wg.id));
            if (result) {
              const isSuccess = result.status === 'SUCCESS';
              return {
                ...wg,
                dataMigrationStatus: isSuccess ? STATUS.STATUS_COMPLETED : STATUS.STATUS_FAILED,
              };
            }
            return wg;
          })
        );
      } else {
        setWorkgroups((prev) =>
          prev.map((wg) => {
            const isSelected = workgroupsToMigrate.some((id) => String(id) === String(wg.id));
            return isSelected ? { ...wg, dataMigrationStatus: STATUS.STATUS_FAILED } : wg;
          })
        );
      }
      setSelectedWorkgroups([]);
      setSelectedTypes((prev) => prev.filter((t) => t !== 'workgroups'));
      setBanner({
        visible: true,
        type: 'Critical',
        message: error?.message || 'Workgroup data migration failed.',
      });
      // eslint-disable-next-line no-console
      console.error(`Workgroup migration failed: ${error.message}`);
    } finally {
      setMigratingWorkgroups(false);
    }
  };

  const executeDataMigration = async (executeJobs) => {
    setShowExecuteDialog(false);
    setMigrating(true);
    try {
      if (!sourceID) {
        return;
      }

      const objectIds = selectedApps.map((id) => parseInt(id, 10));

      const response = await dispatch(
        bulkDataMigrate({
          sourceId: sourceID,
          objectType: OBJECT_TYPE_LABELS.APPLICATION,
          objectIds,
          executeJobTriggers: executeJobs,
        })
      ).unwrap();

      // Check if response indicates job creation (pending) or completion
      const isPending =
        response?.message?.includes('Import Job Created') ||
        response?.message?.includes('Job Created');

      setApplications((prev) =>
        prev.map((app) =>
          selectedApps.includes(app.id)
            ? {
                ...app,
                dataMigrationStatus: isPending
                  ? STATUS.STATUS_IN_PROGRESS
                  : STATUS.STATUS_COMPLETED,
              }
            : app
        )
      );

      handleClearAll();
      setBanner({
        visible: true,
        type: 'Success',
        message: 'Application data migration completed successfully.',
      });
    } catch (error) {
      setBanner({
        visible: true,
        type: 'Critical',
        message: error?.message || 'Application data migration failed.',
      });
      // eslint-disable-next-line no-console
      console.error(`Data migration failed: ${error.message}`);
    } finally {
      setMigrating(false);
    }
  };

  const handleIdentityMigration = async () => {
    setIdentityMigrationStatus({ loading: true });
    try {
      const response = await dispatch(migrateIdentities(sourceID)).unwrap();
      setIdentityMigrationStatus({
        success: true,
        message: `${response.connectionName || ''} ${
          response.message || 'Identity migration completed successfully!'
        }`,
      });
      dispatch(fetchSourceStatus(sourceID));
    } catch (error) {
      setIdentityMigrationStatus({
        success: false,
        message: error.message || UI_LABELS.IDENTITY_MIGRATION_FAILED,
      });
    }
  };

  const showBottomBar = false;

  // eslint-disable-next-line no-nested-ternary
  const loadingText = migrating
    ? DESCRIPTIONS.MIGRATING_APPLICATIONS
    : migratingWorkgroups
    ? 'Migrating workgroups...'
    : DESCRIPTIONS.MIGRATING_IDENTITIES;

  if (loading) {
    return (
      <div className={classes.loader}>
        <Loader kind="loop" size="large" format="inline" color="brand" />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <AlertBanner
        colorTheme={banner.type}
        title={banner.type === 'Success' ? 'Success' : 'Error'}
        description={banner.message}
        isVisible={banner.visible}
        isFloating
        position={{ type: 'fixed', top: '68px', right: '16px' }}
        onCancel={() => setBanner((prev) => ({ ...prev, visible: false }))}
        shouldAutoDismiss
        autoDismissDelay={5000}
        isCloseButtonVisible
        className={classes.alertBanner}
      />
      <div className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          {TITLES.DATA_MIGRATION}
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Migrate data objects from {config.name} to Saviynt
        </Typography>
      </div>

      <div className={`${classes.cardList} ${showBottomBar ? classes.cardListWithBar : ''}`}>
        <DataTypeCard
          type={TITLES.IDENTITIES}
          title={TITLES.IDENTITIES}
          description={`Migrate identity data from ${config.name} to Saviynt.`}
          onMigrate={handleIdentityMigration}
          isMigrating={identityMigrationStatus?.loading}
          isMigrateDisabled={identityMigrationCompleted}
          status={
            identityMigrationStatus && !identityMigrationStatus.loading
              ? identityMigrationStatus
              : null
          }
        />

        {applications?.length > 0 && (
          <DataTypeCard
            type={TITLES.APPLICATIONS_DATA}
            title={TITLES.APPLICATIONS_DATA}
            description={`Migrate accounts and entitlements for selected applications from ${config.name} to Saviynt.`}
            onMigrate={handleMigrate}
            isMigrating={migrating}
            isMigrateDisabled={selectedApps.length === 0}
          >
            <ApplicationSelector
              applications={applications}
              selectedApps={selectedApps}
              onAppSelect={handleAppSelect}
              showApplications={showApplications}
              onToggle={() => setShowApplications(!showApplications)}
              hasEligible={applications.some(
                (app) =>
                  (app.dataMigrationStatus === STATUS.STATUS_PENDING ||
                    app.dataMigrationStatus == null) &&
                  app.status === STATUS.STATUS_COMPLETED
              )}
              onSelectAll={() => {
                const eligibleApps = applications
                  .filter(
                    (app) =>
                      (app.dataMigrationStatus === STATUS.STATUS_PENDING ||
                        app.dataMigrationStatus == null) &&
                      app.status === STATUS.STATUS_COMPLETED
                  )
                  .map((app) => app.id);
                setSelectedApps(eligibleApps);
                if (!selectedTypes.includes('applications')) {
                  setSelectedTypes((prev) => [...prev, 'applications']);
                }
              }}
              onClear={() => {
                setSelectedApps([]);
                setSelectedTypes((prev) => prev.filter((t) => t !== 'applications'));
              }}
            />
          </DataTypeCard>
        )}

        {workgroups?.length > 0 && (
          <DataTypeCard
            type={TITLES.WORKGROUPS_DATA}
            title={TITLES.WORKGROUPS_DATA}
            description={`Migrate workgroups from ${config.name} to Saviynt.`}
            onMigrate={handleWorkgroupMigrate}
            isMigrating={migratingWorkgroups}
            isMigrateDisabled={selectedWorkgroups.length === 0}
          >
            <ApplicationSelector
              applications={workgroups}
              selectedApps={selectedWorkgroups}
              onAppSelect={handleWorkgroupSelect}
              showApplications={showWorkgroups}
              onToggle={() => setShowWorkgroups(!showWorkgroups)}
              completedLabel={UI_LABELS.MEMBERSHIPS_MIGRATED}
              onSelectAll={() => {
                const eligible = workgroups
                  .filter(
                    (wg) =>
                      (wg.dataMigrationStatus === STATUS.STATUS_PENDING ||
                        wg.dataMigrationStatus == null) &&
                      wg.status === STATUS.STATUS_COMPLETED
                  )
                  .map((wg) => wg.id);
                setSelectedWorkgroups(eligible);
                if (!selectedTypes.includes('workgroups')) {
                  setSelectedTypes((prev) => [...prev, 'workgroups']);
                }
              }}
              onClear={() => {
                setSelectedWorkgroups([]);
                setSelectedTypes((prev) => prev.filter((t) => t !== 'workgroups'));
              }}
            />
          </DataTypeCard>
        )}
      </div>

      {(migrating || migratingWorkgroups || identityMigrationStatus?.loading) && (
        <div className={classes.overlay}>
          <div className={classes.loadingModal}>
            <Loader kind="loop" size="large" format="inline" color="brand" />
            <Typography variant="body1" className={classes.loadingText}>
              {loadingText}
            </Typography>
          </div>
        </div>
      )}

      <ExecuteDialog
        isOpen={showExecuteDialog}
        onClose={() => setShowExecuteDialog(false)}
        onExecute={executeDataMigration}
        migrating={migrating}
      />
    </div>
  );
}

DataMigration.propTypes = {
  sourceType: PropTypes.string.isRequired,
  sourceID: PropTypes.string,
};

DataMigration.defaultProps = {
  sourceID: null,
};

export default DataMigration;
